import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { TagBadge } from './TagBadge';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagPickerProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  error?: string;
}

export const TagPicker: React.FC<TagPickerProps> = memo(({
  tags,
  selectedTagIds,
  onToggleTag,
  error,
}) => {
  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const selectedName = useMemo(() => {
    if (selectedTagIds.length === 0) return null;
    const tag = tags.find((t) => selectedSet.has(t.id));
    return tag?.name ?? null;
  }, [tags, selectedTagIds, selectedSet]);

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={styles.label}>Tag</Text>
        <Text style={[styles.counter, selectedName ? styles.counterMax : undefined]}>
          {selectedName ?? 'Select a tag'}
        </Text>
      </View>

      <View style={styles.tagGrid}>
        {tags.map((tag) => {
          const isSelected = selectedSet.has(tag.id);
          return (
            <TagBadge
              key={tag.id}
              tag={{ name: tag.name, color: tag.color }}
              size="medium"
              isActive={isSelected}
              onPress={() => onToggleTag(tag.id)}
            />
          );
        })}
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
});

TagPicker.displayName = 'TagPicker';

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  counter: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  counterMax: {
    color: SEMANTIC_COLORS.interactive.primary,
    fontWeight: '600',
  },
  tagGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  errorText: {
    fontSize: 12,
    color: SEMANTIC_COLORS.interactive.danger,
    marginTop: 2,
  },
});
